import {
  AttachMoney,
  CheckCircleOutline,
  RemoveCircleOutline,
} from "@mui/icons-material";
import { ApiIncrementalityTestResult, ApiMetric } from "@incendium/api";
import { Alert, Box, Grid, Stack, Typography } from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import { useNotification } from "Hooks";
import { useSelectedProject } from "Hooks/useSelectedProject";
import { formatCurrency as formatCurrencyValue } from "features/analytics/services/formatters/currencyFormatter";
import { getCurrencySymbol } from "features/currency";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { buildResultInsightCopy } from "../helpers/copy";
import { METRIC_ICON_MAP, METRIC_LABEL_MAP } from "../helpers/metricOptions";
import { useIncrementalitySetupContext } from "../providers/IncrementalitySetupProvider";
import { readIncrementalityResult } from "../services/incrementalityService";
import IncrementalityPerformanceChart from "./IncrementalityPerformanceChart";
import { MetricIconBubble, SectionSubTitle, SectionTitle } from "./styled";

const formatSignedCurrency = (
  value: number | undefined,
  currencySymbol: string,
) => {
  if (value == null) return "-";
  const absolute = formatCurrencyValue(Math.abs(value), currencySymbol);
  return `${value < 0 ? "-" : ""}${absolute}`;
};

const formatPercent = (value?: number) => {
  if (value == null) return "-";
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
};

const formatRoi = (value?: number) => {
  if (value == null) return "-";
  return `${value > 0 ? "+" : ""}${value.toFixed(2)}x`;
};

function IncrementalityResultsTab() {
  const { incrementalityTest } = useIncrementalitySetupContext();
  const { selectedProject } = useSelectedProject();
  const { showErrorNotification } = useNotification();
  const { testId } = useParams<{ testId: string }>();
  const [loading, setLoading] = useState(false);
  const [incrementalityResult, setIncrementalityResult] =
    useState<ApiIncrementalityTestResult | null>(null);

  const primaryMetricId = incrementalityTest.primaryMetric as ApiMetric;
  const metricLabel = METRIC_LABEL_MAP[primaryMetricId] || "Primary metric";
  const testCity = incrementalityTest.testRegionCity || "the test region";
  const impactPercentage =
    incrementalityResult?.primaryMetric?.impactPercentage;
  const primaryMetricResult = incrementalityResult?.primaryMetric;
  const primarySignificant = !!primaryMetricResult?.statisticallySignificant;
  const primaryPValue = primaryMetricResult?.pValue;
  const primaryCILower = primaryMetricResult?.confidenceIntervalLower;
  const primaryCIUpper = primaryMetricResult?.confidenceIntervalUpper;
  const supportingMetrics = (incrementalityTest.supportingMetrics || []).filter(
    (m) => m !== ApiMetric.METRIC_NOT_SET,
  ) as ApiMetric[];
  const supportingMetricResultsMap = new Map(
    (incrementalityResult?.supportingMetrics || []).map((result) => [
      result.metric as ApiMetric,
      result,
    ]),
  );
  const insightCopy = buildResultInsightCopy({
    impactPercentage,
    testCity,
    metricLabel,
  });
  const financialSummary = incrementalityResult?.financialSummary;
  const currencySymbol = selectedProject?.currency
    ? getCurrencySymbol(selectedProject.currency)
    : "£";
  const numericTestId =
    testId && testId !== "new" && Number.isFinite(Number(testId))
      ? Number(testId)
      : undefined;

  useEffect(() => {
    if (!selectedProject?.id || !numericTestId) {
      setIncrementalityResult(null);
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const result = await readIncrementalityResult(
          selectedProject.id!,
          numericTestId,
        );
        if (cancelled) return;
        setIncrementalityResult(result);
      } catch (error) {
        if (cancelled) return;
        setIncrementalityResult(null);
        showErrorNotification("Failed to load incrementality result");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [numericTestId, selectedProject?.id, showErrorNotification]);

  return (
    <Stack spacing={2.5}>
      <GlassCard boxProps={{ p: 2 }} noShadow>
        <Stack spacing={2}>
          <Box>
            <SectionTitle>Primary metric</SectionTitle>
            <SectionSubTitle>
              Result compared with expected performance.
            </SectionSubTitle>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <MetricIconBubble $size={48}>
                  {METRIC_ICON_MAP[primaryMetricId] || (
                    <AttachMoney fontSize="large" sx={{ fontSize: 48 }} />
                  )}
                </MetricIconBubble>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {metricLabel}
                  </Typography>
                  <Typography
                    variant="h1"
                    color={
                      (impactPercentage || 0) > 0 ? "success.main" : "error.main"
                    }
                  >
                    {loading
                      ? "Calculating..."
                      : impactPercentage == null
                        ? "-"
                        : `${impactPercentage > 0 ? "+" : ""}${impactPercentage.toFixed(1)}%`}
                  </Typography>
                  <Typography
                    variant="body1"
                    color={
                      (impactPercentage || 0) > 0 ? "success.main" : "error.main"
                    }
                    sx={{ textTransform: "lowercase" }}
                  >
                    {impactPercentage == null
                      ? "pending"
                      : impactPercentage > 0
                        ? "increase"
                        : "decrease"}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack spacing={0.75} sx={{ pt: { md: 0.5 } }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {primarySignificant ? (
                    <CheckCircleOutline
                      sx={{ color: "success.main", fontSize: 16 }}
                    />
                  ) : (
                    <RemoveCircleOutline
                      sx={{ color: "text.secondary", fontSize: 16 }}
                    />
                  )}
                  <Typography variant="body2" color="text.primary">
                    {loading
                      ? "Calculating significance..."
                      : primaryPValue == null
                        ? "Significance pending"
                        : primarySignificant
                          ? "Statistically significant"
                          : "Not statistically significant"}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  95% confidence
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {loading
                    ? "..."
                    : primaryPValue == null
                      ? "-"
                      : `p=${primaryPValue.toFixed(3)} | CI ${primaryCILower?.toFixed(2) ?? "-"}% to ${primaryCIUpper?.toFixed(2) ?? "-"}%`}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.primary">
            {loading ? "Preparing insight..." : insightCopy}
          </Typography>
        </Stack>
      </GlassCard>

      <GlassCard boxProps={{ p: 2 }} noShadow>
        <Stack spacing={2}>
          <Box>
            <SectionTitle>Financial impact (vs. expected)</SectionTitle>
            <SectionSubTitle>
              All values compared to what would have happened without pausing
              campaigns
            </SectionSubTitle>
          </Box>
          <Grid container>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                pr: 2,
                borderRight: { md: 1 },
                borderColor: "divider",
                mb: { xs: 1.5, md: 0 },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Estimated revenue impact
              </Typography>
              <Typography
                variant="h2"
                color="error.main"
                fontWeight={700}
                mb={1}
              >
                {loading
                  ? "..."
                  : formatSignedCurrency(
                      financialSummary?.estimatedRevenueImpactValue,
                      currencySymbol,
                    )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {loading
                  ? "-"
                  : formatPercent(
                      financialSummary?.estimatedRevenueImpactPercentage,
                    )}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                px: { xs: 0, md: 2 },
                borderRight: { md: 1 },
                borderColor: "divider",
                mb: { xs: 1.5, md: 0 },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Reduced ad spend
              </Typography>
              <Typography
                variant="h2"
                color="primary.main"
                fontWeight={700}
                mb={1}
              >
                {loading
                  ? "..."
                  : formatSignedCurrency(
                      financialSummary?.reducedAdSpendValue,
                      currencySymbol,
                    )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {loading
                  ? "-"
                  : formatPercent(financialSummary?.reducedAdSpendPercentage)}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                px: { xs: 0, md: 2 },
                borderRight: { md: 1 },
                borderColor: "divider",
                mb: { xs: 1.5, md: 0 },
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Net incremental impact
              </Typography>
              <Typography
                variant="h2"
                color="error.main"
                fontWeight={700}
                mb={1}
              >
                {loading
                  ? "..."
                  : formatSignedCurrency(
                      financialSummary?.netIncrementalImpactValue,
                      currencySymbol,
                    )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {loading
                  ? "-"
                  : formatPercent(
                      financialSummary?.netIncrementalImpactPercentage,
                    )}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3} sx={{ pl: { xs: 0, md: 2 } }}>
              <Typography variant="caption" color="text.secondary">
                ROI of change
              </Typography>
              <Typography
                variant="h2"
                color="error.main"
                fontWeight={700}
                mb={1}
              >
                {loading ? "..." : formatRoi(financialSummary?.roiOfChange)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Return per {currencySymbol}1 spent
              </Typography>
            </Grid>
          </Grid>
          <Alert severity="info">
            Negative impact means pausing campaigns reduced overall performance
            beyond cost savings.
          </Alert>
        </Stack>
      </GlassCard>

      <IncrementalityPerformanceChart
        incrementalityTest={incrementalityTest}
        primaryMetricId={primaryMetricId}
      />

      <GlassCard boxProps={{ p: 2 }} noShadow>
        <Stack spacing={2}>
          <Box>
            <SectionTitle>Supporting metrics</SectionTitle>
            <SectionSubTitle>
              Directional read across selected supporting metrics.
            </SectionSubTitle>
          </Box>
          <Grid container spacing={1.5}>
            {supportingMetrics.map((metricId) => {
              const metricResult = supportingMetricResultsMap.get(metricId) as
                | {
                    impactPercentage?: number;
                    statisticallySignificant?: boolean;
                    pValue?: number;
                  }
                | undefined;
              const metricImpact = metricResult?.impactPercentage;
              const isPositive = (metricImpact || 0) > 0;
              const isSignificant = !!metricResult?.statisticallySignificant;

              return (
                <Grid key={metricId} item xs={12} md={4}>
                  <Box
                    sx={{
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      p: 1.5,
                      height: "100%",
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MetricIconBubble $size={28}>
                          {METRIC_ICON_MAP[metricId] || <AttachMoney />}
                        </MetricIconBubble>
                        <Typography variant="subtitle2">
                          {METRIC_LABEL_MAP[metricId] || "Metric"}
                        </Typography>
                      </Stack>
                      <Box>
                        <Typography
                          variant="h4"
                          color={isPositive ? "success.main" : "error.main"}
                          fontWeight={700}
                        >
                          {loading || metricImpact == null
                            ? "-"
                            : `${metricImpact > 0 ? "+" : ""}${metricImpact.toFixed(1)}%`}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={isPositive ? "success.main" : "error.main"}
                          sx={{ textTransform: "lowercase" }}
                        >
                          {metricImpact == null
                            ? "pending"
                            : isPositive
                              ? "increase"
                              : "decrease"}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {isSignificant ? (
                          <CheckCircleOutline
                            sx={{ color: "success.main", fontSize: 16 }}
                          />
                        ) : (
                          <RemoveCircleOutline
                            sx={{ color: "text.secondary", fontSize: 16 }}
                          />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {metricResult?.pValue == null
                            ? "Significance pending"
                            : isSignificant
                              ? `Statistically significant (p=${metricResult.pValue.toFixed(3)})`
                              : `Not statistically significant (p=${metricResult.pValue.toFixed(3)})`}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Stack>
      </GlassCard>
    </Stack>
  );
}

export default IncrementalityResultsTab;
